import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { HiPlus, HiTrash, HiPencil, HiEye, HiXMark } from 'react-icons/hi2';
import { getOrders, createOrder, updateOrderStatus, deleteOrder } from '../../services/api';

const STATUS_OPTIONS = ['pending', 'confirmed', 'dispatched', 'delivered', 'cancelled'];
const STATUS_CLASS = {
  pending: 'badge-yellow', confirmed: 'badge-blue', dispatched: 'badge-purple',
  delivered: 'badge-green', cancelled: 'badge-red',
};
const fmt = (n) => new Intl.NumberFormat('en-IN').format(Number(n) || 0);

const EMPTY_ORDER_FORM = {
  customer_id: '', delivery_date: '', delivery_address: '', discount: '', notes: '',
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_ORDER_FORM);
  const [items, setItems] = useState([]);
  const [itemInput, setItemInput] = useState({ product_id: '', quantity: '' });
  const [saving, setSaving] = useState(false);

  const [viewOrder, setViewOrder] = useState(null);
  const [statusEdit, setStatusEdit] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchOrders = useCallback(() => {
    setLoading(true);
    const params = {
      page, limit,
      status: statusFilter || undefined,
      from: dateFrom || undefined,
      to: dateTo || undefined,
    };
    getOrders(params)
      .then((res) => {
        const data = res.data;
        setOrders(Array.isArray(data) ? data : data.orders || []);
        setTotal(typeof data.total === 'number' ? data.total : (Array.isArray(data) ? data.length : 0));
      })
      .catch(() => toast.error('Failed to load orders.'))
      .finally(() => setLoading(false));
  }, [page, statusFilter, dateFrom, dateTo]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const openModal = () => { setForm(EMPTY_ORDER_FORM); setItems([]); setItemInput({ product_id: '', quantity: '' }); setModalOpen(true); };
  const closeModal = () => setModalOpen(false);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const addItem = () => {
    if (!itemInput.product_id || !itemInput.quantity) { toast.error('Enter product ID and quantity.'); return; }
    const qty = parseInt(itemInput.quantity);
    if (isNaN(qty) || qty < 1) { toast.error('Quantity must be a positive number.'); return; }
    setItems((prev) => {
      const existing = prev.find((i) => String(i.product_id) === String(itemInput.product_id));
      if (existing) return prev.map((i) => String(i.product_id) === String(itemInput.product_id) ? { ...i, quantity: i.quantity + qty } : i);
      return [...prev, { product_id: parseInt(itemInput.product_id), quantity: qty }];
    });
    setItemInput({ product_id: '', quantity: '' });
  };

  const removeItem = (pid) => setItems((prev) => prev.filter((i) => i.product_id !== pid));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) { toast.error('Customer ID is required.'); return; }
    if (items.length === 0) { toast.error('Add at least one item.'); return; }
    setSaving(true);
    const payload = {
      customer_id: parseInt(form.customer_id),
      items,
      delivery_date: form.delivery_date || undefined,
      delivery_address: form.delivery_address || undefined,
      discount: form.discount ? Number(form.discount) : 0,
      notes: form.notes || undefined,
    };
    try {
      await createOrder(payload);
      toast.success('Order created.');
      closeModal();
      fetchOrders();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to create order.');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await updateOrderStatus(orderId, newStatus);
      toast.success('Status updated.');
      setStatusEdit((prev) => ({ ...prev, [orderId]: false }));
      fetchOrders();
    } catch {
      toast.error('Failed to update status.');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteOrder(deleteTarget.id || deleteTarget._id);
      toast.success('Order deleted.');
      setDeleteTarget(null);
      fetchOrders();
    } catch {
      toast.error('Failed to delete order.');
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.ceil(total / limit) || 1;

  return (
    <div className="content-area">
      <div className="page-header">
        <h1 className="page-title">Orders</h1>
        <button className="btn btn-primary" onClick={openModal}><HiPlus /> New Order</button>
      </div>

      {/* Filter Bar */}
      <div className="filter-bar">
        <select className="form-control" style={{ width: '160px' }} value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <label className="form-label" style={{ margin: 0, whiteSpace: 'nowrap' }}>From:</label>
          <input type="date" className="form-control" style={{ width: '150px' }} value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <label className="form-label" style={{ margin: 0, whiteSpace: 'nowrap' }}>To:</label>
          <input type="date" className="form-control" style={{ width: '150px' }} value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
        </div>
        {(statusFilter || dateFrom || dateTo) && (
          <button className="btn btn-secondary btn-sm" onClick={() => { setStatusFilter(''); setDateFrom(''); setDateTo(''); setPage(1); }}>
            <HiXMark /> Clear
          </button>
        )}
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Items</th>
                  <th>Status</th>
                  <th>Total Rs.</th>
                  <th>Order Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8' }}>No orders found.</td></tr>
                ) : (
                  orders.map((o) => {
                    const oid = o.id || o._id;
                    const isEditingStatus = statusEdit[oid];
                    return (
                      <tr key={oid}>
                        <td style={{ fontWeight: 600 }}>{o.order_number || `#${oid}`}</td>
                        <td>{o.customer_name || o.customer?.name || `ID: ${o.customer_id}`}</td>
                        <td>{o.items_count ?? (Array.isArray(o.items) ? o.items.length : '-')}</td>
                        <td>
                          {isEditingStatus ? (
                            <select
                              className="form-control"
                              style={{ width: '130px', padding: '0.25rem 0.5rem', fontSize: '0.8125rem' }}
                              defaultValue={o.status}
                              onChange={(e) => handleStatusChange(oid, e.target.value)}
                              autoFocus
                              onBlur={() => setStatusEdit((p) => ({ ...p, [oid]: false }))}
                            >
                              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                          ) : (
                            <span className={`badge ${STATUS_CLASS[o.status] || 'badge-gray'}`}>{o.status}</span>
                          )}
                        </td>
                        <td>Rs.{fmt(o.total_amount)}</td>
                        <td>{o.order_date ? new Date(o.order_date).toLocaleDateString('en-IN') : '-'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                            <button className="btn btn-secondary btn-sm" title="View" onClick={() => setViewOrder(o)}><HiEye /></button>
                            <button className="btn btn-secondary btn-sm" title="Edit Status" onClick={() => setStatusEdit((p) => ({ ...p, [oid]: true }))}>
                              <HiPencil />
                            </button>
                            <button className="btn btn-danger btn-sm" title="Delete" onClick={() => setDeleteTarget(o)}><HiTrash /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
        {!loading && total > limit && (
          <div style={{ padding: '0.75rem 1.5rem', borderTop: '1px solid var(--border)' }}>
            <div className="pagination">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button key={p} className={p === page ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          </div>
        )}
      </div>

      {/* New Order Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className="modal" style={{ maxWidth: '620px' }}>
            <div className="modal-header">
              <span className="modal-title">New Order</span>
              <button className="btn btn-secondary btn-sm" onClick={closeModal}><HiXMark /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Customer ID *</label>
                  <input className="form-control" type="number" name="customer_id" value={form.customer_id} onChange={handleFormChange} placeholder="Enter customer ID" min="1" required />
                </div>

                {/* Add Items */}
                <div className="card" style={{ marginBottom: '1rem', padding: '1rem' }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.75rem', fontSize: '0.875rem' }}>Order Items</div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', alignItems: 'flex-end' }}>
                    <div className="form-group" style={{ flex: 1, margin: 0 }}>
                      <label className="form-label">Product ID</label>
                      <input className="form-control" type="number" placeholder="Product ID" value={itemInput.product_id}
                        onChange={(e) => setItemInput((p) => ({ ...p, product_id: e.target.value }))} min="1" />
                    </div>
                    <div className="form-group" style={{ width: '100px', margin: 0 }}>
                      <label className="form-label">Quantity</label>
                      <input className="form-control" type="number" placeholder="Qty" value={itemInput.quantity}
                        onChange={(e) => setItemInput((p) => ({ ...p, quantity: e.target.value }))} min="1" />
                    </div>
                    <button type="button" className="btn btn-primary" onClick={addItem} style={{ whiteSpace: 'nowrap' }}>
                      <HiPlus /> Add
                    </button>
                  </div>
                  {items.length > 0 ? (
                    <table style={{ width: '100%', fontSize: '0.8125rem' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: '0.25rem 0.5rem', color: '#475569' }}>Product ID</th>
                          <th style={{ textAlign: 'left', padding: '0.25rem 0.5rem', color: '#475569' }}>Qty</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((it) => (
                          <tr key={it.product_id}>
                            <td style={{ padding: '0.25rem 0.5rem' }}>{it.product_id}</td>
                            <td style={{ padding: '0.25rem 0.5rem' }}>{it.quantity}</td>
                            <td style={{ padding: '0.25rem 0.5rem' }}>
                              <button type="button" className="btn btn-danger btn-sm" onClick={() => removeItem(it.product_id)}>
                                <HiTrash />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-muted" style={{ fontSize: '0.8125rem' }}>No items added yet.</p>
                  )}
                </div>

                <div className="grid grid-2">
                  <div className="form-group">
                    <label className="form-label">Delivery Date</label>
                    <input className="form-control" type="date" name="delivery_date" value={form.delivery_date} onChange={handleFormChange} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Discount (Rs.)</label>
                    <input className="form-control" type="number" name="discount" value={form.discount} onChange={handleFormChange} min="0" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Delivery Address</label>
                  <textarea className="form-control" name="delivery_address" value={form.delivery_address} onChange={handleFormChange} rows={2} />
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-control" name="notes" value={form.notes} onChange={handleFormChange} rows={2} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Creating...' : 'Create Order'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Order Modal */}
      {viewOrder && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setViewOrder(null)}>
          <div className="modal" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <span className="modal-title">Order {viewOrder.order_number || `#${viewOrder.id || viewOrder._id}`}</span>
              <button className="btn btn-secondary btn-sm" onClick={() => setViewOrder(null)}><HiXMark /></button>
            </div>
            <div className="modal-body">
              <div className="grid grid-2" style={{ marginBottom: '1rem' }}>
                <div><span className="text-muted">Customer:</span><br /><strong>{viewOrder.customer_name || `ID: ${viewOrder.customer_id}`}</strong></div>
                <div><span className="text-muted">Status:</span><br /><span className={`badge ${STATUS_CLASS[viewOrder.status] || 'badge-gray'}`}>{viewOrder.status}</span></div>
                <div><span className="text-muted">Total:</span><br /><strong>Rs.{fmt(viewOrder.total_amount)}</strong></div>
                <div><span className="text-muted">Order Date:</span><br />{viewOrder.order_date ? new Date(viewOrder.order_date).toLocaleDateString('en-IN') : '-'}</div>
                {viewOrder.delivery_date && <div><span className="text-muted">Delivery Date:</span><br />{new Date(viewOrder.delivery_date).toLocaleDateString('en-IN')}</div>}
                {viewOrder.delivery_address && <div><span className="text-muted">Delivery Address:</span><br />{viewOrder.delivery_address}</div>}
              </div>
              {Array.isArray(viewOrder.items) && viewOrder.items.length > 0 && (
                <>
                  <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Items</div>
                  <table style={{ width: '100%', fontSize: '0.875rem' }}>
                    <thead><tr><th>Product</th><th>Qty</th><th>Price</th></tr></thead>
                    <tbody>
                      {viewOrder.items.map((it, i) => (
                        <tr key={i}>
                          <td>{it.product_name || `ID: ${it.product_id}`}</td>
                          <td>{it.quantity}</td>
                          <td>Rs.{fmt(it.price || it.unit_price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
              {viewOrder.notes && <p className="text-muted" style={{ marginTop: '0.75rem' }}>Notes: {viewOrder.notes}</p>}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setViewOrder(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setDeleteTarget(null)}>
          <div className="modal" style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <span className="modal-title">Delete Order</span>
              <button className="btn btn-secondary btn-sm" onClick={() => setDeleteTarget(null)}><HiXMark /></button>
            </div>
            <div className="modal-body">
              <p>Delete order <strong>{deleteTarget.order_number || `#${deleteTarget.id || deleteTarget._id}`}</strong>? This cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
